import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { AgentPresence } from "../utils/agent-presence-utils"
import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { Search, SearchX } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"

import { AgentCard } from "./agent-card"

type AgentRosterProps = {
  agents: HostedAgentSummary[]
  presenceById?: ReadonlyMap<string, AgentPresence>
  onOpen: (agent: HostedAgentSummary) => void
}

export function AgentRoster({ agents, presenceById, onOpen }: AgentRosterProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const query = search.trim().toLocaleLowerCase()
  const visible = agents.filter(({ profile }) =>
    [profile.name, profile.title, profile.description].some((text) =>
      text.toLocaleLowerCase().includes(query)
    )
  )

  return (
    <section aria-label={t("agents.roster.title")} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b py-4">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
          {t("agents.roster.team_count", { count: agents.length })}
        </p>
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-3 left-3 size-4"
          />
          <Input
            aria-label={t("agents.roster.search")}
            className="h-10 pl-9"
            placeholder={t("agents.roster.search")}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>
      {visible.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {visible.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              presence={presenceById?.get(agent.id)}
              onOpen={onOpen}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          description={t("agents.roster.search_description")}
          icon={SearchX}
          title={t("agents.roster.no_matches")}
          primaryAction={
            <Button variant="outline" onClick={() => setSearch("")}>
              {t("agents.roster.clear_search")}
            </Button>
          }
        />
      )}
    </section>
  )
}
